import React from 'react';
import { toast } from 'react-toastify';
import { ordersAPI } from '../config/orders-api';
import { getStatusMeta, normalizeStatusValue } from './orderConfigHelpers';

let activeUndoDismiss = null;

export const buildOrderStatusSnapshot = (order = {}) => ({
  orderStatus: normalizeStatusValue(order.orderStatus),
  confirmedOn: order.confirmedOn || null,
  cancelReason: order.cancelReason || '',
  returnReason: order.returnReason || '',
});

export const buildUndoExtras = (snapshot = {}) => ({
  restoreConfirmedOn: snapshot.confirmedOn || null,
  cancelReason: snapshot.cancelReason || '',
  returnReason: snapshot.returnReason || '',
  isUndo: true,
});

const showStatusUndoToast = ({ statusLabel, onUndo }) => {
  if (activeUndoDismiss) {
    activeUndoDismiss();
    activeUndoDismiss = null;
  }

  const toastId = toast(
    ({ closeToast }) => (
      <div className="order-status-undo-toast">
        <span className="order-status-undo-text">
          Changing order status to {statusLabel}
        </span>
        <button
          type="button"
          className="order-status-undo-btn"
          onClick={() => {
            closeToast();
            if (activeUndoDismiss) {
              activeUndoDismiss = null;
            }
            onUndo?.();
          }}
        >
          Undo
        </button>
      </div>
    ),
    {
      position: 'bottom-center',
      autoClose: 3000,
      closeOnClick: false,
      hideProgressBar: false,
      className: 'order-status-undo-toast-container',
      toastId: `status-undo-${Date.now()}`,
    }
  );

  activeUndoDismiss = () => toast.dismiss(toastId);
};

export const performOrderStatusUpdate = async ({
  orderId,
  newStatus,
  snapshot,
  orderStatuses = [],
  negativeOrderStatuses = [],
  extras = {},
  onSuccess,
  onError,
}) => {
  const statusValue = normalizeStatusValue(newStatus);
  const meta = getStatusMeta(statusValue, orderStatuses, negativeOrderStatuses);
  const result = await ordersAPI.updateOrderStatus(orderId, statusValue, extras);

  if (!result.success) {
    const message = result.error || 'Failed to update order status';
    onError?.(message);
    toast.error(message);
    return result;
  }

  onSuccess?.(result);

  showStatusUndoToast({
    statusLabel: meta.label,
    onUndo: async () => {
      const undoStatus = normalizeStatusValue(snapshot?.orderStatus);
      if (!undoStatus) return;

      const undoResult = await ordersAPI.updateOrderStatus(
        orderId,
        undoStatus,
        buildUndoExtras(snapshot)
      );

      if (undoResult.success) {
        onSuccess?.(undoResult, { isUndo: true });
      } else {
        toast.error(undoResult.error || 'Failed to undo status change');
      }
    },
  });

  return result;
};
